/* Add-to-cart hand-off for the PDP — v3 (2026-10-04). Markup: snippets/tee-popup-confirm-add-to-cart.liquid.
   TeeInBlue events: `teeinblue-event-before-cart-added` (validation passed, /cart/add.js about to post),
   `teeinblue-event-after-cart-added` (line added; TeeInBlue still sends its cart-token request right after).
   - Buy now: `globalThis.__tee_buy_now` (set on the Buy now click, cleared on every Add to cart click so a
     failed Buy now attempt can never swallow a later Add to cart). The SDK routes to checkout itself; the
     theme only shows a "Taking you to secure checkout…" toast. Never opens the drawer or the sheet.
   - Desktop (CAFMiniCartDrawer.canOpen()): CAFMiniCartDrawer.showAdding() as soon as the add starts (drawer
     opens with its skeleton), then the unchanged 900 ms settle → fetchRefreshAndOpen() → /cart fallback.
     Without showAdding() (older drawer) the flow is exactly the 2026-05-09 one.
   - Phones (drawer can't open): stay on the page and open the "Added to your cart" sheet immediately
     (skeleton while it loads — `.is-loading`, see caf-rd-pdp-personalize.css §13), filled from /cart.js
     after the same 900 ms settle; /cart.js failure → /cart (old behaviour) while the sheet is still open.
   - Split test "sheet" (2026-10-12, window.CAFExperiments.arm('sheet') — snippets/caf-experiments.liquid):
     a = that sheet as it is; b = checkout-first sheet ("Checkout" posts the cart form with name="checkout"
     like the drawer does, behind the same settle; "View cart"; "Keep shopping" link; full typed text; action
     block pinned when the card must scroll); c = no sheet, phones go to the cart page after the settle (the
     flow before v3). No CAFExperiments / anything unexpected → a.
   - Offer line (all arms with a sheet): a cart whose shippable lines are all ornaments, and that three
     ornaments would take over the free-shipping minimum, reads "Add 2 more ornaments, any designs: 15% off
     all three + free shipping" with a "1 of 3" bar — see getBundleOffer(). Otherwise (also an ornament next
     to any other product that ships) the plain free-shipping line, unchanged.
   - Every shopper action dispatches `caf:added-sheet-action` { action, arm }
     (assets/tracking-events-bridge.js reports it).
   Public API (also used by app.js for the native AJAX form): window.CAFAddedSheet
     { available(), open({ item, cart, settle }), close(), isOpen() }. */
(() => {
  if (window.CAFAddedSheet) return;

  const doc = document;
  const root = doc.documentElement;
  const TEE_ATC_HANDOFF_DELAY_MS = 900;
  const SHEET_ID = 'caf-added-sheet';
  const TOAST_ID = 'caf-pdp-toast';
  const OVERLAY_SOURCE = 'caf-added-sheet';
  const ADD_FAILURE_TIMEOUT_MS = 20000;
  const CHECKOUT_RECOVERY_MS = 10000;

  const getCartUrl = () => {
    const sheet = doc.getElementById(SHEET_ID);
    if (sheet && sheet.dataset.cartUrl) return sheet.dataset.cartUrl;
    const base = globalThis.Shopify?.routes?.root || '/';
    return `${base.endsWith('/') ? base : `${base}/`}cart`;
  };

  const deferTeeAtcHandoff = (callback) => globalThis.setTimeout(callback, TEE_ATC_HANDOFF_DELAY_MS);

  /* ------------------------------------------------------------------ split test "sheet" + events */

  // 'a' today's sheet · 'b' checkout-first sheet · 'c' no sheet (cart page). Anything unexpected → 'a'.
  const getArm = () => {
    try {
      const arm = globalThis.CAFExperiments ? globalThis.CAFExperiments.arm('sheet') : 'a';
      return arm === 'b' || arm === 'c' ? arm : 'a';
    } catch (error) {
      return 'a';
    }
  };

  // One event per shopper action. Only a DOM event here: the tracking bridge listens and reports, nothing
  // is awaited, and a failure can never hold back the sheet or a navigation.
  const track = (action) => {
    try {
      doc.dispatchEvent(new CustomEvent('caf:added-sheet-action', { detail: { action, arm: getArm() } }));
    } catch (error) {
      // reporting only
    }
  };

  /* ------------------------------------------------------------------ buy-now intent */

  globalThis.__tee_buy_now = false;
  doc.addEventListener('teeinblue-event-component-injected', () => {
    globalThis.__tee_buy_now = false;
  });

  let lastTrigger = null;
  doc.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element && event.target.closest('#teeBuynowButton, #teeAtcButton, #preview-teeAtcButton');
      if (!button) return;
      globalThis.__tee_buy_now = button.id === 'teeBuynowButton';
      lastTrigger = button.id === 'preview-teeAtcButton' ? doc.getElementById('teeAtcButton') : button;
      if (!globalThis.__tee_buy_now && !drawerAvailable() && getArm() !== 'c') snapshotCart();
    },
    true,
  );

  /* ------------------------------------------------------------------ desktop drawer */

  const getDrawer = () => globalThis.CAFMiniCartDrawer;

  function drawerAvailable() {
    const drawer = getDrawer();
    if (!drawer || typeof drawer.fetchRefreshAndOpen !== 'function') return false;
    return typeof drawer.canOpen !== 'function' || drawer.canOpen();
  }

  const showDrawerAdding = () => {
    const drawer = getDrawer();
    if (!drawer || typeof drawer.showAdding !== 'function') return false;
    try {
      return drawer.showAdding() !== false;
    } catch (error) {
      return false;
    }
  };

  function openMiniCartAfterTeeAdd() {
    const drawer = getDrawer();
    if (!drawer || typeof drawer.fetchRefreshAndOpen !== 'function') return false;
    if (typeof drawer.canOpen === 'function' && !drawer.canOpen()) return false;

    drawer
      .fetchRefreshAndOpen()
      .then((opened) => {
        if (!opened) globalThis.location.href = getCartUrl();
      })
      .catch(() => {
        globalThis.location.href = getCartUrl();
      });

    return true;
  }

  // The add can still fail after `before-cart-added` (e.g. stock, network): TeeInBlue then renders
  // `.tee-error` under the buttons. Close a drawer we opened early so the message is visible.
  let failureObserver = null;
  let failureTimer = 0;
  let drawerOpenedEarly = false;

  const stopFailureWatch = () => {
    if (failureObserver) failureObserver.disconnect();
    failureObserver = null;
    globalThis.clearTimeout(failureTimer);
  };

  const onAddFailed = () => {
    stopFailureWatch();
    hideToast();
    if (drawerOpenedEarly) {
      drawerOpenedEarly = false;
      const drawer = getDrawer();
      if (drawer && typeof drawer.close === 'function') drawer.close();
    }
    doc.dispatchEvent(new CustomEvent('caf:pdp-add-failed'));
  };

  const watchForFailure = () => {
    stopFailureWatch();
    const actions = doc.getElementById('teeFormActions');
    if (actions) {
      // Only an error rendered AFTER the add started counts (a stale "Some fields are required" from an
      // earlier attempt may still be in the DOM).
      const existing = new Set(actions.querySelectorAll('.tee-form-responses .tee-error'));
      failureObserver = new MutationObserver(() => {
        const errors = Array.from(actions.querySelectorAll('.tee-form-responses .tee-error'));
        if (errors.some((node) => !existing.has(node))) onAddFailed();
      });
      failureObserver.observe(actions, { childList: true, subtree: true });
    }
    failureTimer = globalThis.setTimeout(onAddFailed, ADD_FAILURE_TIMEOUT_MS);
  };

  /* ------------------------------------------------------------------ toast (buy now) */

  let toastTimer = 0;
  const showToast = (text) => {
    const toast = doc.getElementById(TOAST_ID);
    if (!toast) return;
    const label = toast.querySelector('[data-caf-toast-text]');
    if (label) label.textContent = text;
    toast.hidden = false;
    globalThis.clearTimeout(toastTimer);
    toastTimer = globalThis.setTimeout(hideToast, 15000);
  };

  function hideToast() {
    const toast = doc.getElementById(TOAST_ID);
    if (toast) toast.hidden = true;
    globalThis.clearTimeout(toastTimer);
  }

  globalThis.addEventListener('pagehide', hideToast);

  /* ------------------------------------------------------------------ money + line helpers */

  const getSheet = () => doc.getElementById(SHEET_ID);

  const formatMoney = (cents) => {
    const sheet = getSheet();
    const format = (sheet && sheet.dataset.moneyFormat) || doc.body?.dataset?.moneyFormat || '${{amount}}';
    if (globalThis.Shopify && typeof globalThis.Shopify.formatMoney === 'function') {
      try {
        return globalThis.Shopify.formatMoney(cents, format);
      } catch (error) {
        // fall through
      }
    }
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  };

  const isGiftCardLine = (item) => Boolean(item && item.gift_card);

  const normalizeUrl = (value) => {
    let url = String(value || '').trim();
    if (!url) return '';
    if ((url.startsWith('"') && url.endsWith('"')) || (url.startsWith("'") && url.endsWith("'"))) url = url.slice(1, -1);
    url = url.replace(/\\\//g, '/');
    if (url.startsWith('//')) url = `${globalThis.location.protocol}${url}`;
    return url;
  };

  // Same rule as sections/header.liquid `mini_cart_preview_url`: first property whose key mentions
  // preview / image / mockup / thumbnail / render and whose value is a URL (TeeInBlue `_customization_image`).
  const getPreviewUrl = (item) => {
    const entries = Object.entries((item && item.properties) || {});
    for (const [rawKey, rawValue] of entries) {
      const key = String(rawKey || '').toLowerCase();
      const value = String(rawValue || '').trim();
      if (!value) continue;
      if (!/(preview|image|mockup|thumbnail|render)/.test(key)) continue;
      if (value.includes('http') || value.startsWith('//') || value.includes('data:image')) return normalizeUrl(value);
    }
    return '';
  };

  const sizedImage = (url, width) => {
    if (!url) return '';
    try {
      const parsed = new URL(url, globalThis.location.origin);
      if (/cdn\.shopify\.com|\/cdn\/shop\//.test(parsed.href)) parsed.searchParams.set('width', String(width));
      return parsed.href;
    } catch (error) {
      return url;
    }
  };

  // Typed-text line — same rules as assets/mini-cart-drawer.js getLineText + snippets/cart-line-details.liquid
  // (keep in sync): "Enter …" shown, "Choose …" hidden, other keys only with a text word and no choice word.
  const TEXT_WORDS = ['name', 'date', 'year', 'message', 'text', 'quote', 'initial', 'wording', 'title', 'birthday'];
  const CHOICE_WORDS = ['color', 'colour', 'font', 'tone', 'style', 'skin', 'hair'];
  const getDetails = (item) => {
    const values = [];
    Object.entries((item && item.properties) || {}).forEach(([rawKey, rawValue]) => {
      const key = String(rawKey ?? '').trim();
      const value = String(rawValue ?? '').trim();
      if (!key || !value || key.startsWith('_')) return;
      const keyLower = key.toLowerCase();
      if (keyLower.includes('customization') || keyLower.includes('preview')) return;
      const valueLower = value.toLowerCase();
      if (valueLower === 'on' || valueLower.startsWith('//') || valueLower.includes('://') || valueLower.includes('data:image') || valueLower.includes('/uploads/')) return;
      const isText = keyLower.startsWith('enter ')
        ? true
        : !keyLower.startsWith('choose ') && TEXT_WORDS.some((word) => keyLower.includes(word)) && !CHOICE_WORDS.some((word) => keyLower.includes(word));
      if (isText) values.push(value);
    });
    return values.length ? `Text: ${values.join(' · ')}` : '';
  };

  const getVariantText = (item) => {
    const options = Array.isArray(item && item.options_with_values) ? item.options_with_values : [];
    const values = options
      .map((option) => String(option.value || '').trim())
      .filter((value) => value && !/^(n\/a|default title)$/i.test(value));
    if (values.length) return values.join(' · ');
    const title = String((item && item.variant_title) || '').trim();
    return /^default title$/i.test(title) ? '' : title.split(' / ').filter((part) => !/^n\/a$/i.test(part.trim())).join(' · ');
  };

  /* ------------------------------------------------------------------ which line was just added */

  let snapshot = null; // { at, lines: Map(key → quantity) }

  const fetchCart = () =>
    globalThis
      .fetch((getSheet() && getSheet().dataset.cartJs) || `${getCartUrl()}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`cart.js ${response.status}`);
        return response.json();
      });

  function snapshotCart() {
    if (!getSheet()) return;
    const at = Date.now();
    fetchCart()
      .then((cart) => {
        snapshot = { at, lines: new Map((cart.items || []).map((item) => [item.key, Number(item.quantity || 0)])) };
      })
      .catch(() => {
        snapshot = null;
      });
  }

  const pickAddedLine = (cart, hint) => {
    const items = (cart && cart.items) || [];
    if (!items.length) return null;
    if (hint && hint.key) {
      const byKey = items.find((item) => item.key === hint.key);
      if (byKey) return byKey;
    }
    if (hint && hint.variant_id) {
      const byVariant = items.find((item) => item.variant_id === hint.variant_id);
      if (byVariant) return byVariant;
    }
    if (snapshot && Date.now() - snapshot.at < 120000) {
      const changed = items.find((item) => !snapshot.lines.has(item.key) || Number(item.quantity || 0) > snapshot.lines.get(item.key));
      if (changed) return changed;
    }
    const productId = Number((getSheet() && getSheet().dataset.productId) || 0);
    return items.find((item) => Number(item.product_id) === productId) || items[0];
  };

  /* ------------------------------------------------------------------ "Buy 3" offer line */

  // The offer is the PDP promo-banner rule ("Buy 3 ornaments, 15% off", a Shopify automatic discount). Its tag
  // and its own description come from the theme settings (data-bundle-* on the sheet); the two numbers are
  // read from that description — never written here. No "Buy N" and "N%" in it → no offer line.
  const getBundleConfig = (sheet) => {
    const data = sheet.dataset;
    const tag = String(data.bundleTag || '').trim().toLowerCase();
    const words = String(data.bundleOffer || '');
    const min = Number((words.match(/\bbuy\s+(\d+)\b/i) || [])[1] || 0);
    const percent = Number((words.match(/(\d+(?:\.\d+)?)\s*%/) || [])[1] || 0);
    return { enabled: data.bundleEnabled === 'true' && Boolean(tag) && min >= 2 && percent > 0 && percent < 100, tag, min, percent };
  };

  // cart.js has no product tags. Known without a request: this page's product (Liquid) and the lines the
  // drawer printed for the cart the page loaded with (data-bundle-match, same tag — read once, before any
  // script repaints the drawer). Any other product → /products/<handle>.js once per page view, as in
  // assets/mini-cart-drawer.js ensureBundleTags().
  const bundleProducts = new Map(); // product id → counts for the offer
  const bundleRequests = new Map();

  const seedBundleProducts = () => {
    const sheet = getSheet();
    if (!sheet) return;
    const config = getBundleConfig(sheet);
    if (sheet.dataset.productId) bundleProducts.set(String(sheet.dataset.productId), sheet.dataset.bundleMatch === 'true');
    doc.querySelectorAll('[data-cart-form="mini-cart"]').forEach((form) => {
      if (String(form.dataset.bundleTag || '').trim().toLowerCase() !== config.tag) return;
      form.querySelectorAll('[data-cart-item][data-product-id]').forEach((line) => {
        const { productId, bundleMatch } = line.dataset;
        if (!bundleProducts.has(productId) && (bundleMatch === 'true' || bundleMatch === 'false')) bundleProducts.set(productId, bundleMatch === 'true');
      });
    });
  };
  seedBundleProducts();

  // null when every line is known already (the usual case); else a promise for the missing lookups.
  const lookUpBundleProducts = (sheet, cart) => {
    const config = getBundleConfig(sheet);
    if (!config.enabled) return null;
    const base = globalThis.Shopify?.routes?.root || '/';
    const pending = [];
    ((cart && cart.items) || []).forEach((item) => {
      const id = String(item.product_id || '');
      if (!id || bundleProducts.has(id) || isGiftCardLine(item) || !item.handle) return;
      if (!bundleRequests.has(id)) {
        bundleRequests.set(
          id,
          globalThis
            .fetch(`${base.endsWith('/') ? base : `${base}/`}products/${encodeURIComponent(item.handle)}.js`, { headers: { Accept: 'application/json' } })
            .then((response) => (response.ok ? response.json() : null))
            .then((product) => {
              const tags = Array.isArray(product?.tags) ? product.tags : String(product?.tags || '').split(',');
              bundleProducts.set(id, tags.some((tag) => String(tag).trim().toLowerCase() === config.tag));
            })
            .catch(() => {
              bundleProducts.set(id, false);
            }),
        );
      }
      pending.push(bundleRequests.get(id));
    });
    return pending.length ? Promise.all(pending) : null;
  };

  // A discount code that is applied to the cart: the automatic rule does not combine with order codes, so the
  // offer line must not promise it. Same test as the drawer (getComboState in mini-cart-drawer.js: applicable
  // codes + code-type discount rows) and as the Liquid twins (cart.discount_applications of type
  // discount_code). A code that is only saved for later (cart.js `applicable: false`, or the `discount_code`
  // cookie of a /discount/CODE link before it applies) is invisible to Liquid, so it does not count here
  // either — otherwise the sheet would say one thing and the cart page the other for the same cart.
  const hasDiscountCode = (cart) => {
    if ((Array.isArray(cart.discount_codes) ? cart.discount_codes : []).some((discount) => discount && discount.code && discount.applicable !== false)) return true;
    const isCode = (application) => Boolean(application) && application.type === 'discount_code';
    if ((cart.cart_level_discount_applications || []).some(isCode)) return true;
    return (cart.items || []).some((item) => (item.line_level_discount_allocations || []).some((allocation) => isCode(allocation.discount_application)));
  };

  // Which line the cart gets — null = the plain free-shipping line. Owner rule 2026-10-12: the offer line only
  // when the cart is under the free-shipping minimum, carries no discount code, holds at least one ornament,
  // and `min` ornaments at the rule's price would clear the minimum on their own: lowest ornament unit price
  // in the cart × min × (100 − percent) ÷ 100, cents rounded down, ≥ minimum. `done` only once the automatic
  // discount really is on the ornaments and shipping really is free.
  // Mixed carts keep the plain line: every line that ships must be an ornament. One ornament + a $24.99
  // tumbler is $4.02 from free shipping — a nearer goal than two more ornaments. Gift cards and other lines
  // that do not ship are left out of this test. The minimum is an amount in the shop currency, so a cart in
  // another currency keeps the plain line (as the product-page card does, snippets/caf-pdp-banner.liquid).
  // ONE rule, four places — keep them identical (scripts/tests/bundle-offer.test.cjs compares them):
  // `offer_combo` in sections/cart-template.liquid, `mc_combo` in snippets/cart-drawer.liquid,
  // getComboState() in assets/mini-cart-drawer.js, and here.
  const getBundleOffer = (sheet, cart, physical, freeMin) => {
    const config = getBundleConfig(sheet);
    if (!config.enabled || hasDiscountCode(cart)) return null;
    const shopCurrency = String(sheet.dataset.shopCurrency || '');
    if (shopCurrency && cart.currency && cart.currency !== shopCurrency) return null;
    let quantity = 0;
    let cheapest = 0;
    let applied = false;
    let unknown = false;
    let otherShips = false;
    (cart.items || []).forEach((item) => {
      if (isGiftCardLine(item)) return;
      const match = bundleProducts.get(String(item.product_id));
      if (match === undefined) unknown = true;
      if (match !== true) {
        if (item.requires_shipping !== false) otherShips = true;
        return;
      }
      quantity += Number(item.quantity || 0);
      const unit = Number(item.final_price ?? item.price ?? 0);
      if (!cheapest || unit < cheapest) cheapest = unit;
      const allocations = Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : item.discounts || [];
      if (allocations.some((allocation) => Number(allocation.amount || 0) > 0)) applied = true; // never a code: see hasDiscountCode
    });
    if (unknown || otherShips || !quantity) return null;
    if (quantity >= config.min) return applied && physical >= freeMin ? { done: true, percent: config.percent } : null;
    if (physical >= freeMin) return null;
    const afterRule = Math.floor(((100 - config.percent) * cheapest * config.min) / 100);
    if (afterRule < freeMin) return null;
    return { done: false, quantity, remaining: config.min - quantity, min: config.min, percent: config.percent };
  };

  /* ------------------------------------------------------------------ sheet rendering */

  const setText = (node, text) => {
    if (node) node.textContent = text;
  };

  // Guarded writes for the offer line (same value → no write).
  const setAttr = (node, name, value) => {
    if (!node) return;
    if (value == null) {
      if (node.hasAttribute(name)) node.removeAttribute(name);
    } else if (node.getAttribute(name) !== value) {
      node.setAttribute(name, value);
    }
  };

  const setHtml = (node, html) => {
    if (node && node.innerHTML !== html) node.innerHTML = html;
  };

  // TeeInBlue renders the design preview (`_customization_image`) a few seconds AFTER the add, so the URL can
  // 404 at first: show the product photo meanwhile and swap in the preview once it loads (bounded retries).
  let previewTimer = 0;
  const showPreviewWhenReady = (img, url, fallback, attempt = 0) => {
    globalThis.clearTimeout(previewTimer);
    const probe = new Image();
    probe.onload = () => {
      // use the exact URL that loaded (an earlier 404 of the bare URL may be cached)
      if (img.isConnected) img.setAttribute('src', probe.src);
    };
    probe.onerror = () => {
      if (attempt < 6 && open) previewTimer = globalThis.setTimeout(() => showPreviewWhenReady(img, url, fallback, attempt + 1), 1500);
    };
    probe.src = attempt ? `${url}${url.includes('?') ? '&' : '?'}caf_r=${attempt}` : url;
  };

  const renderItem = (item) => {
    const sheet = getSheet();
    if (!sheet || !item) return;
    const preview = getPreviewUrl(item);
    const fallback = sizedImage(item.image || (item.featured_image && item.featured_image.url) || '', 240) || sheet.dataset.productImage;
    const img = sheet.querySelector('[data-caf-sheet-img]');
    if (img) {
      img.setAttribute('alt', '');
      if (fallback && img.getAttribute('src') !== fallback) img.setAttribute('src', fallback);
      if (preview) showPreviewWhenReady(img, preview, fallback);
    }

    setText(sheet.querySelector('[data-caf-sheet-name]'), item.product_title || item.title || sheet.dataset.productTitle || '');
    // Gift card: the variant title is the amount ("$25.00") and repeated the price right under it — say what it
    // is instead, same words as the cart line (snippets/cart-drawer.liquid, sections/cart-template.liquid).
    const variant = isGiftCardLine(item) ? 'Digital gift card · sent by email' : getVariantText(item);
    const meta = sheet.querySelector('[data-caf-sheet-meta]');
    setText(meta, variant);
    if (meta) meta.hidden = !variant;

    const detailsNode = sheet.querySelector('[data-caf-sheet-details]');
    const details = sheet.dataset.showDetails === 'false' ? '' : getDetails(item);
    setText(detailsNode, details);
    if (detailsNode) detailsNode.hidden = !details;

    const now = Number(item.final_line_price ?? item.line_price ?? item.final_price ?? item.price ?? 0);
    const was = Number(item.original_line_price ?? 0);
    setText(sheet.querySelector('[data-caf-sheet-now]'), formatMoney(now));
    const wasNode = sheet.querySelector('[data-caf-sheet-was]');
    if (wasNode) {
      wasNode.hidden = !(was > now);
      setText(wasNode, was > now ? formatMoney(was) : '');
    }
    const qtyNode = sheet.querySelector('[data-caf-sheet-qty]');
    const quantity = Number(item.quantity || 1);
    if (qtyNode) {
      qtyNode.hidden = quantity <= 1;
      setText(qtyNode, quantity > 1 ? `Qty ${quantity}` : '');
    }
  };

  const renderCart = (cart) => {
    const sheet = getSheet();
    if (!sheet || !cart) return;
    const count = Number(cart.item_count || 0);
    setText(sheet.querySelector('[data-caf-sheet-count]'), `Your cart: ${count} ${count === 1 ? 'item' : 'items'}`);
    setText(sheet.querySelector('[data-caf-sheet-subtotal]'), formatMoney(cart.total_price));

    lastCart = cart;
    const pending = lookUpBundleProducts(sheet, cart);
    if (!pending) {
      renderShip(sheet, cart);
      return;
    }
    // Rare: a line whose product the page does not know yet (added from a cross-sell). Look it up first so
    // the line never says one thing and then another; after 2.5 s it is decided with what is known.
    const ship = sheet.querySelector('[data-caf-sheet-ship]');
    if (ship) ship.hidden = true;
    let decided = false;
    const decide = () => {
      if (decided || !open || lastCart !== cart) return;
      decided = true;
      renderShip(sheet, cart);
      updatePinned();
    };
    pending.then(decide);
    globalThis.setTimeout(decide, 2500);
  };

  const ALL_WORDS = { 2: 'both', 3: 'all three', 4: 'all four', 5: 'all five', 6: 'all six' };

  function renderShip(sheet, cart) {
    // Free shipping: physical subtotal only — gift cards never count (owner decision; Shopify excludes
    // them at checkout too). Same math as assets/mini-cart-drawer.js renderPerks().
    const freeMin = Number(sheet.dataset.freeShippingMinimum || 0);
    let giftTotal = 0;
    let hasPhysical = false;
    (cart.items || []).forEach((item) => {
      if (isGiftCardLine(item)) giftTotal += Number(item.final_line_price || 0);
      else hasPhysical = true;
    });
    const physical = Math.max(0, Number(cart.total_price || 0) - giftTotal);
    const ship = sheet.querySelector('[data-caf-sheet-ship]');
    if (!ship) return;
    const show = freeMin > 0 && hasPhysical;
    ship.hidden = !show;
    if (!show) return;

    const offer = getBundleOffer(sheet, cart, physical, freeMin);
    const steps = Boolean(offer) && !offer.done; // "1 of 3" bar instead of the money bar
    const remaining = freeMin - physical;
    const unlocked = offer ? offer.done : remaining <= 0;
    const percentage = unlocked ? 100 : Math.max(4, Math.min(100, Math.floor((physical / freeMin) * 100)));
    ship.classList.toggle('is-unlocked', unlocked);

    let line;
    if (steps) {
      const gain = `<span class="caf-added-sheet__ship-gain">${offer.percent}% off ${ALL_WORDS[offer.min] || `all ${offer.min}`} + free shipping</span>`;
      line = offer.remaining === 1 ? `Add <b>1 more ornament</b>: ${gain}` : `Add <b>${offer.remaining} more ornaments</b>, any designs: ${gain}`;
    } else if (offer) {
      // Two phrases. CSS draws the middle dot between them only while they share a row (caf-rd-pdp-personalize.css
      // §13 `.caf-added-sheet__ship-done`); on a phone they stack, like the same row on the cart page.
      line = `<b class="caf-added-sheet__ship-done"><span>${offer.percent}% off your ornaments applied</span> <span>Free shipping unlocked</span></b>`;
    } else {
      line = unlocked ? '<b>You’ve unlocked free shipping</b>' : `Add <b>${formatMoney(remaining)}</b> more for free shipping`;
    }
    setHtml(ship.querySelector('[data-caf-sheet-ship-text]'), line);

    const steppedOf = steps ? `${offer.quantity} of ${offer.min}` : '';
    const bar = ship.querySelector('[role="progressbar"]');
    setAttr(bar, 'aria-label', steps ? `Ornaments toward ${offer.percent}% off and free shipping` : 'Progress to free shipping');
    setAttr(bar, 'aria-valuemax', steps ? String(offer.min) : '100');
    setAttr(bar, 'aria-valuenow', steps ? String(offer.quantity) : String(percentage));
    setAttr(bar, 'aria-valuetext', steps ? steppedOf : null);
    const fill = ship.querySelector('[data-caf-sheet-ship-fill]');
    const width = steps ? `${Math.round((offer.quantity / offer.min) * 10000) / 100}%` : `${percentage}%`;
    if (fill && fill.style.width !== width) fill.style.width = width;
    const count = ship.querySelector('[data-caf-sheet-ship-count]');
    if (count) {
      if (count.textContent !== steppedOf) count.textContent = steppedOf;
      if (count.hidden !== !steps) count.hidden = !steps;
    }
  }

  const resetSheet = () => {
    const sheet = getSheet();
    if (!sheet) return;
    const img = sheet.querySelector('[data-caf-sheet-img]');
    if (img && sheet.dataset.productImage) img.setAttribute('src', sheet.dataset.productImage);
    setText(sheet.querySelector('[data-caf-sheet-name]'), sheet.dataset.productTitle || '');
    const meta = sheet.querySelector('[data-caf-sheet-meta]');
    if (meta) {
      meta.hidden = false;
      meta.textContent = ' ';
    }
    const details = sheet.querySelector('[data-caf-sheet-details]');
    if (details) details.hidden = true;
    setText(sheet.querySelector('[data-caf-sheet-now]'), ' ');
    const was = sheet.querySelector('[data-caf-sheet-was]');
    if (was) was.hidden = true;
    const qty = sheet.querySelector('[data-caf-sheet-qty]');
    if (qty) qty.hidden = true;
    setText(sheet.querySelector('[data-caf-sheet-count]'), ' ');
    setText(sheet.querySelector('[data-caf-sheet-subtotal]'), ' ');
    const ship = sheet.querySelector('[data-caf-sheet-ship]');
    if (ship) ship.hidden = true;
  };

  /* ------------------------------------------------------------------ sheet open / close / focus */

  let open = false;
  let settled = true;
  let settleWaiters = [];
  let overlayAddedHere = false;
  let returnFocus = null;
  let closeTimer = 0;
  let pendingGoTimer = 0;
  let lastCart = null;
  let cartWaiting = false; // a "View cart" tap is waiting for the settle
  // "Checkout" (checkout-first sheet): idle → waiting (for the settle) → go (our own click) → sent.
  let checkoutState = 'idle';
  let checkoutTimer = 0;

  const setOverlay = (on) => {
    if (globalThis.CAFChromeOverlay && typeof globalThis.CAFChromeOverlay.set === 'function') {
      globalThis.CAFChromeOverlay.set(OVERLAY_SOURCE, on);
    } else if (on && !root.classList.contains('caf-overlay-open')) {
      root.classList.add('caf-overlay-open');
      overlayAddedHere = true;
    } else if (!on && overlayAddedHere) {
      root.classList.remove('caf-overlay-open');
      overlayAddedHere = false;
    }
    root.classList.toggle('caf-added-sheet-open', on);
  };

  // Which button group the sheet shows. 'b' needs its group in the markup (the snippet prints it only while
  // checkout may skip the cart page, like the drawer) — otherwise today's sheet. snippets/caf-experiments.liquid
  // applies the same gate, so arm('sheet') is not 'b' then; this check only covers markup and script out of step.
  const applyVariant = (sheet) => {
    const variant = getArm() === 'b' && sheet.querySelector('[data-caf-sheet-actions="b"]') ? 'b' : 'a';
    if (sheet.dataset.variant === variant) return;
    sheet.dataset.variant = variant;
    sheet.classList.toggle('caf-added-sheet--checkout-first', variant === 'b');
    sheet.querySelectorAll('[data-caf-sheet-actions]').forEach((group) => {
      group.hidden = group.dataset.cafSheetActions !== variant;
    });
  };

  // Checkout-first sheet: when the card + totals do not fit (in-app browsers, long typed text) the middle
  // scrolls and the action block shows its edge. `.is-scroll` only paints (hairline + shadow), so the
  // measurement cannot feed back into itself.
  const updatePinned = () => {
    const sheet = getSheet();
    const panel = sheet && sheet.querySelector('.caf-added-sheet__panel');
    const body = sheet && sheet.querySelector('[data-caf-sheet-body]');
    if (!panel || !body) return;
    const scrolls = sheet.dataset.variant === 'b' && body.scrollHeight > body.clientHeight + 1;
    if (panel.classList.contains('is-scroll') !== scrolls) panel.classList.toggle('is-scroll', scrolls);
  };

  let pinnedFrame = 0;
  globalThis.addEventListener('resize', () => {
    if (!open) return;
    globalThis.cancelAnimationFrame(pinnedFrame);
    pinnedFrame = globalThis.requestAnimationFrame(updatePinned);
  });

  // Busy look of "Checkout". The button is never `disabled`: a disabled submitter drops name="checkout" from
  // the POST and Shopify would answer with the cart page (same rule as the drawer, mini-cart-drawer.js).
  const setCheckoutBusy = (busy) => {
    const sheet = getSheet();
    const button = sheet && sheet.querySelector('[data-caf-sheet-checkout]');
    if (!button) return;
    const label = button.querySelector('[data-caf-sheet-checkout-label]');
    if (label && !label.dataset.defaultLabel) label.dataset.defaultLabel = label.textContent.trim();
    button.classList.toggle('is-busy', busy);
    setAttr(button, 'aria-busy', busy ? 'true' : null);
    setAttr(button, 'aria-disabled', busy ? 'true' : null);
    if (label) label.textContent = busy ? 'Opening checkout…' : label.dataset.defaultLabel || 'Checkout';
    const spin = button.querySelector('.caf-spin');
    if (busy && !spin) button.insertAdjacentHTML('afterbegin', '<span class="caf-spin" aria-hidden="true"></span>');
    else if (!busy && spin) spin.remove();
  };

  // Also the recovery: still on the page a while after the tap (connection dropped, navigation blocked) →
  // the button works again.
  const resetCheckout = () => {
    globalThis.clearTimeout(checkoutTimer);
    if (checkoutState === 'idle') return;
    checkoutState = 'idle';
    setCheckoutBusy(false);
  };

  const clearCartWaiting = () => {
    cartWaiting = false;
    const sheet = getSheet();
    if (sheet) sheet.querySelectorAll('[data-caf-sheet-cart]').forEach((link) => link.removeAttribute('aria-busy'));
  };

  // Only one exit can wait for the settle — the last tap wins.
  const clearWaitingExit = () => {
    globalThis.clearTimeout(pendingGoTimer);
    settleWaiters = [];
    if (cartWaiting) clearCartWaiting();
    if (checkoutState === 'waiting') resetCheckout();
  };

  // Back from the cart page or checkout through the back/forward cache: no button left spinning.
  globalThis.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    clearCartWaiting();
    resetCheckout();
  });

  const markSettled = () => {
    settled = true;
    const waiters = settleWaiters;
    settleWaiters = [];
    waiters.forEach((resolve) => resolve());
  };

  const focusables = (panel) =>
    Array.from(panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(
      (node) => node.getClientRects().length > 0,
    );

  const onKeydown = (event) => {
    if (!open) return;
    const sheet = getSheet();
    const panel = sheet && sheet.querySelector('.caf-added-sheet__panel');
    if (!panel) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close('escape');
      return;
    }
    if (event.key !== 'Tab') return;
    const nodes = focusables(panel);
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && (doc.activeElement === first || !panel.contains(doc.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && doc.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const onSheetClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const closer = event.target.closest('[data-caf-sheet-close]');
    if (closer) {
      event.preventDefault();
      close(closer.getAttribute('data-caf-sheet-close'));
      return;
    }

    const checkoutButton = event.target.closest('[data-caf-sheet-checkout]');
    if (checkoutButton) {
      if (checkoutState === 'go') {
        // our own click once TeeInBlue has settled → the browser posts the form, name="checkout" included
        checkoutState = 'sent';
        return;
      }
      if (checkoutState !== 'idle') {
        event.preventDefault(); // already on its way — no second submit
        return;
      }
      track('checkout');
      if (!settled) clearWaitingExit();
      setCheckoutBusy(true);
      checkoutTimer = globalThis.setTimeout(resetCheckout, CHECKOUT_RECOVERY_MS);
      if (settled) {
        checkoutState = 'sent';
        return;
      }
      // TeeInBlue's post-add cart-token request must finish before we leave the page (900 ms settle).
      event.preventDefault();
      checkoutState = 'waiting';
      const go = () => {
        globalThis.clearTimeout(pendingGoTimer);
        if (!open || checkoutState !== 'waiting') return;
        checkoutState = 'go';
        checkoutButton.click();
      };
      settleWaiters.push(go);
      pendingGoTimer = globalThis.setTimeout(go, 2500);
      return;
    }

    const cartLink = event.target.closest('[data-caf-sheet-cart]');
    if (!cartLink) return;
    if (cartWaiting) {
      event.preventDefault(); // second tap while the first waits for the settle
      return;
    }
    track('view_cart');
    if (settled) return;
    // TeeInBlue's post-add cart-token request must finish before we leave the page (900 ms settle).
    event.preventDefault();
    clearWaitingExit();
    cartWaiting = true;
    cartLink.setAttribute('aria-busy', 'true');
    const go = () => {
      globalThis.clearTimeout(pendingGoTimer);
      if (!open || !cartWaiting) return;
      globalThis.location.href = cartLink.getAttribute('href') || getCartUrl();
    };
    settleWaiters.push(go);
    pendingGoTimer = globalThis.setTimeout(go, 2500);
  };

  let bound = false;
  const bindSheet = (sheet) => {
    if (bound) return;
    bound = true;
    sheet.addEventListener('click', onSheetClick);
    doc.addEventListener('keydown', onKeydown, true);
  };

  const openSheet = ({ item = null, cart = null, settle = 0 } = {}) => {
    const sheet = getSheet();
    if (!sheet) return false;
    bindSheet(sheet);
    globalThis.clearTimeout(closeTimer);

    const wasOpen = open;
    if (!open) {
      const active = doc.activeElement;
      returnFocus = active instanceof HTMLElement && active !== doc.body ? active : lastTrigger;
    }
    open = true;
    resetSheet();
    applyVariant(sheet);
    clearWaitingExit();
    clearCartWaiting();
    resetCheckout();
    sheet.classList.toggle('is-loading', !cart);
    if (item) renderItem(item);
    if (cart) {
      renderItem(pickAddedLine(cart, item));
      renderCart(cart);
    }
    settled = !(settle > 0);
    if (settle > 0) globalThis.setTimeout(markSettled, settle);

    sheet.hidden = false;
    setOverlay(true);
    const body = sheet.querySelector('[data-caf-sheet-body]');
    if (body && body.scrollTop) body.scrollTop = 0;
    updatePinned();
    globalThis.requestAnimationFrame(() => {
      sheet.classList.add('is-open');
      const title = sheet.querySelector('#caf-added-sheet-title');
      if (title) title.focus({ preventScroll: true });
    });
    if (!wasOpen) track('open');
    return true;
  };

  const fill = (hint) =>
    fetchCart().then((cart) => {
      const sheet = getSheet();
      if (sheet && open) {
        renderItem(pickAddedLine(cart, hint));
        renderCart(cart);
        sheet.classList.remove('is-loading');
        updatePinned();
        markSettled();
        // TeeInBlue blurs the page after its add; keep keyboard / screen-reader focus inside the dialog.
        if (!sheet.contains(doc.activeElement)) {
          const title = sheet.querySelector('#caf-added-sheet-title');
          if (title) title.focus({ preventScroll: true });
        }
      }
      // Keep the header badge (and the hidden drawer markup) in sync with the new cart — also when the shopper
      // closed the sheet while it was still loading (it is on screen from the first moment, 2026-10-12).
      try {
        const drawer = getDrawer();
        if (drawer && typeof drawer.refresh === 'function') drawer.refresh(cart);
      } catch (error) {
        // not fatal — the sheet already shows the right numbers
      }
      return cart;
    });

  // /cart.js failed: the cart page shows what the sheet could not — unless the shopper already closed the sheet.
  const goToCartIfOpen = () => {
    if (open) globalThis.location.href = getCartUrl();
  };

  const CLOSE_ACTIONS = { outside: 'close_outside', x: 'close_x', keep: 'keep_shopping', escape: 'close_escape' };

  // reason = how the shopper closed it (the value of data-caf-sheet-close, or 'escape'); none for API calls.
  function close(reason) {
    const sheet = getSheet();
    if (!sheet || !open) return;
    open = false;
    if (typeof reason === 'string' && CLOSE_ACTIONS[reason]) track(CLOSE_ACTIONS[reason]);
    sheet.classList.remove('is-open');
    setOverlay(false);
    // Closing cancels a "View cart" / "Checkout" that was waiting for the settle.
    clearWaitingExit();
    resetCheckout();
    settled = true;
    closeTimer = globalThis.setTimeout(() => {
      sheet.hidden = true;
    }, 300);
    const target = returnFocus && returnFocus.isConnected && returnFocus.getClientRects().length ? returnFocus : null;
    returnFocus = null;
    if (target) target.focus({ preventScroll: true });
  }

  /* ------------------------------------------------------------------ TeeInBlue events */

  doc.addEventListener('teeinblue-event-before-cart-added', () => {
    if (globalThis.__tee_buy_now) {
      showToast('Taking you to secure checkout…');
      watchForFailure();
      return;
    }
    if (drawerAvailable()) {
      drawerOpenedEarly = showDrawerAdding();
      if (drawerOpenedEarly) watchForFailure();
    }
  });

  // After the Tee customizer confirms an add: desktop → CAF mini-cart drawer; phones → the sheet.
  // Buy Now still bypasses this — the SDK takes care of routing to checkout.
  doc.addEventListener('teeinblue-event-after-cart-added', () => {
    stopFailureWatch();
    if (globalThis.__tee_buy_now) {
      globalThis.__tee_buy_now = false;
      return;
    }

    if (drawerAvailable()) {
      if (!drawerOpenedEarly) showDrawerAdding();
      drawerOpenedEarly = false;
      deferTeeAtcHandoff(() => {
        if (!openMiniCartAfterTeeAdd()) globalThis.location.href = getCartUrl();
      });
      return;
    }

    // Split test arm "c": no sheet — the cart page after the settle, the phone flow before v3 (and what
    // already happens when the sheet markup is missing).
    const forward = getArm() === 'c';
    if (forward) track('forward_cart');
    if (forward || !openSheet({ settle: TEE_ATC_HANDOFF_DELAY_MS })) {
      deferTeeAtcHandoff(() => {
        globalThis.location.href = getCartUrl();
      });
      return;
    }
    deferTeeAtcHandoff(() => {
      fill(null).catch(goToCartIfOpen);
    });
  });

  window.CAFAddedSheet = {
    available: () => Boolean(getSheet()),
    // item = /cart/add.js line (native form), cart = /cart.js payload; without cart we fetch it.
    open: (options = {}) => {
      if (getArm() === 'c' && getSheet()) {
        // Split test arm "c" — native form (app.js): same destination as the TeeInBlue path, no sheet.
        track('forward_cart');
        globalThis.setTimeout(() => {
          globalThis.location.href = getCartUrl();
        }, options.settle || 0);
        return true;
      }
      const opened = openSheet({ item: options.item || null, cart: options.cart || null, settle: options.settle || 0 });
      if (opened && !options.cart) fill(options.item || null).catch(goToCartIfOpen);
      return opened;
    },
    close: () => close(),
    isOpen: () => open,
  };
})();
